/**
 * Web Snap & Stash service layer (FR-W1–FR-W3): axios client for the shipped
 * three-turn extraction API. Mirrors the chat-service pattern (relative
 * paths through the dev proxy, Firebase ID token as Bearer, ApiResponse
 * envelope). Every failure maps to the discriminated ExtractionServiceError
 * union and is RETURNED — never thrown — so the snap-stash store owns flow
 * control. Wire shapes come from @stash/shared-types exclusively.
 */
import axios from 'axios';
import type {
  ImageExtractionCreateRequest,
  ImageExtractionCreateResponse,
  ImageExtractionEnrichRequest,
  ImageExtractionEnrichResponse,
  ImageExtractionCommitRequest,
  ImageExtractionCommitResponse,
  ImageExtractionCommitAlreadyCommitted,
  ImageExtractionSnapshotResponse,
} from '@stash/shared-types';
import { auth } from './firebase';

const BASE_URL = '/api/v1/image-extractions';
/** Server budgets are ~12s extract / ~18s enrich; cap every call client-side. */
const CLIENT_TIMEOUT_MS = 30_000;

export type ExtractionServiceError =
  | { kind: 'auth' }
  | { kind: 'payloadTooLarge' }
  | { kind: 'unsupportedImage' }
  | { kind: 'quotaExceeded'; resetsAt?: string }
  | { kind: 'sessionExpired' }
  | { kind: 'alreadyCommitted'; body: ImageExtractionCommitAlreadyCommitted }
  | { kind: 'serverError' }
  | { kind: 'network' };

export type ExtractionResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: ExtractionServiceError };

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

/** JSON API error body emitted by the server's error middleware. */
interface GuardErrorBody {
  errors?: Array<{ status?: number; title?: string; detail?: string; meta?: { resetsAt?: string } }>;
}

interface AxiosishError {
  response?: { status?: number; data?: GuardErrorBody & Partial<ApiResponse<unknown>> };
  request?: unknown;
}

function mapError(error: unknown): ExtractionServiceError {
  const err = error as AxiosishError;
  const status = err.response?.status;
  switch (status) {
    case 413: return { kind: 'payloadTooLarge' };
    case 422: return { kind: 'unsupportedImage' };
    case 410: return { kind: 'sessionExpired' };
    case 429: {
      const resetsAt = err.response?.data?.errors?.[0]?.meta?.resetsAt;
      return { kind: 'quotaExceeded', ...(resetsAt ? { resetsAt } : {}) };
    }
    case 409: {
      // The commit route serializes alreadyCommitted as an ApiResponse whose
      // data carries the original vendorId/stashCardId — success, not failure.
      const body = err.response?.data?.data as ImageExtractionCommitAlreadyCommitted | undefined;
      if (body?.status === 'alreadyCommitted') {
        return { kind: 'alreadyCommitted', body };
      }
      return { kind: 'serverError' };
    }
    case undefined:
      return { kind: 'network' };
    default:
      return { kind: 'serverError' };
  }
}

export class ImageExtractionService {
  /** FR-W2: exactly one idempotency key per session, reused on every attempt. */
  private idempotencyKeys = new Map<string, string>();

  private async authHeaders(): Promise<Record<string, string> | null> {
    const user = auth.currentUser;
    if (!user) return null;
    const idToken = await user.getIdToken();
    return {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
    };
  }

  private async post<T>(url: string, body: unknown): Promise<ExtractionResult<T>> {
    const headers = await this.authHeaders();
    if (!headers) return { ok: false, error: { kind: 'auth' } };
    try {
      const response = await axios.post<ApiResponse<T>>(url, body, {
        headers,
        timeout: CLIENT_TIMEOUT_MS,
      });
      return { ok: true, data: response.data.data };
    } catch (error) {
      return { ok: false, error: mapError(error) };
    }
  }

  async createExtraction(
    request: ImageExtractionCreateRequest
  ): Promise<ExtractionResult<ImageExtractionCreateResponse>> {
    return this.post<ImageExtractionCreateResponse>(BASE_URL, request);
  }

  async enrich(
    sessionId: string,
    request: ImageExtractionEnrichRequest
  ): Promise<ExtractionResult<ImageExtractionEnrichResponse>> {
    return this.post<ImageExtractionEnrichResponse>(`${BASE_URL}/${sessionId}/enrich`, request);
  }

  async commit(
    sessionId: string,
    request: Omit<ImageExtractionCommitRequest, 'idempotencyKey'>
  ): Promise<ExtractionResult<ImageExtractionCommitResponse>> {
    let idempotencyKey = this.idempotencyKeys.get(sessionId);
    if (!idempotencyKey) {
      idempotencyKey = crypto.randomUUID();
      this.idempotencyKeys.set(sessionId, idempotencyKey);
    }
    const wireRequest: ImageExtractionCommitRequest = { ...request, idempotencyKey };
    return this.post<ImageExtractionCommitResponse>(`${BASE_URL}/${sessionId}/commit`, wireRequest);
  }

  async getSnapshot(
    sessionId: string
  ): Promise<ExtractionResult<ImageExtractionSnapshotResponse>> {
    const headers = await this.authHeaders();
    if (!headers) return { ok: false, error: { kind: 'auth' } };
    try {
      const response = await axios.get<ApiResponse<ImageExtractionSnapshotResponse>>(
        `${BASE_URL}/${sessionId}`,
        { headers, timeout: CLIENT_TIMEOUT_MS });
      return { ok: true, data: response.data.data };
    } catch (error) {
      return { ok: false, error: mapError(error) };
    }
  }
}

export const imageExtractionService = new ImageExtractionService();
